import { ActionIcon, Group, Paper, Tooltip } from '@mantine/core';
import { DocumentReference } from '@medplum/fhirtypes';
import { IconDownload, IconFile, IconTrash } from '@tabler/icons-react';
import { AttachmentDisplay } from './medplum-react/AttachmentDisplay/AttachmentDisplay';
import { AttachmentDisplayContext } from './medplum-react/AttachmentDisplay/AttachmentDisplayContext';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { downloadFile } from '@/pages/ainigma/patient/files-tab/utils/report-files.utils';
import { useMedplum } from '@medplum/react';
import { useBlobUrls } from '@/utils/blob.utils';

export interface DocumentReferenceDisplayProps {
  file: DocumentReference;
  readonly?: boolean;
  onDelete?: (documentId: string) => void;
}
export const DocumentReferenceDisplay = ({
  file,
  readonly = false,
  onDelete,
}: DocumentReferenceDisplayProps) => {
  const { t } = useTranslation(['patient-pages', 'files-tab']);
  const medplum = useMedplum();
  const { createBlobUrl } = useBlobUrls();

  const attachment = file.content?.[0]?.attachment;

  // Convert base64 data to URL if needed
  const attachmentWithUrl =
    attachment.data && !attachment.url
      ? {
          ...attachment,
          url: createBlobUrl(
            attachment.data,
            attachment.contentType || 'application/octet-stream'
          ),
        }
      : attachment;

  const onClickDownload = (): void => {
    const url = attachment?.url;
    if (!url) {
      return;
    }
    downloadFile({
      medplum,
      url,
      fileName: attachment?.title || 'document.pdf',
      t: t as unknown as TFunction<'files-tab'>,
    });
  };

  return (
    <Paper p="xs" bg="gray.0" radius="sm">
      <Group justify="space-between">
        <Group wrap="nowrap">
          <IconFile size={16} />
          <AttachmentDisplayContext.Provider value={{ preview: false }}>
            <AttachmentDisplay value={attachmentWithUrl} />
          </AttachmentDisplayContext.Provider>
        </Group>
        <Group>
          <Tooltip
            label={t('reportCards.download', {
              defaultValue: 'Download',
            })}
          >
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={onClickDownload}
              aria-label={t('reportCards.download', {
                defaultValue: 'Download',
              })}
            >
              <IconDownload size={14} />
            </ActionIcon>
          </Tooltip>
          {!readonly && (
            <ActionIcon
              size="sm"
              color="red"
              variant="subtle"
              onClick={() => onDelete?.(file.id ?? '')}
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Paper>
  );
};
